import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Briefcase, Calendar, ShoppingBag, Tag, Crown, Zap, Building2, Clock, Globe } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { getPlaceholderImage } from '../utils/placeholderImages';
import { formatSalary } from '../utils/salaryFormatter';
import { formatJobType, getJobTypeBadgeColor } from '../utils/jobTypeFormatter';
import { formatCondition } from '../utils/conditionFormatter';
import { getListingUrl } from '../utils/slugify';
import { cardThumbnail, cardThumbnailSrcSet, cardThumbnailSquare, cardThumbnailSquareSrcSet } from '../utils/cloudinaryOptimize';
import OpeningHoursStatus from './OpeningHoursStatus';

// Format category slug to display name (e.g. "markets_fairs" -> "Markets Fairs")
const formatCategory = (cat) => {
  if (!cat) return '';
  return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Strip HTML tags and decode entities for card description preview
const stripTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

const ListingCard = ({ listing, uniformAspect = false, index, hideImages = false, hideCategoryBadge = false }) => {
  const isAboveFold = typeof index === 'number' && index < 3;
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3.5 w-3.5 ${star <= rating ? 'fill-[#D4A017] text-[#D4A017]' : 'text-slate-200'}`}
          />
        ))}
      </div>
    );
  };

  const getTypeIcon = () => {
    switch (listing.listing_type) {
      case 'business': return <ShoppingBag className="h-3.5 w-3.5" />;
      case 'event': return <Calendar className="h-3.5 w-3.5" />;
      case 'job': return <Briefcase className="h-3.5 w-3.5" />;
      case 'classified': return <Tag className="h-3.5 w-3.5" />;
      default: return <MapPin className="h-3.5 w-3.5" />;
    }
  };

  const getTypeColor = () => {
    switch (listing.listing_type) {
      case 'business': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'event': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'job': return 'bg-green-50 text-green-700 border-green-200';
      case 'classified': return 'bg-orange-50 text-orange-700 border-orange-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  // Generate SEO-friendly URL
  const listingUrl = getListingUrl(listing);

  // Format event date - handles single dates and ranges
  const formatTime12 = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    return `${h}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // For same year ranges: "20 January - 30 January 2026"
  // For different year ranges: "20 December 2025 - 10 January 2026"
  const formatEventDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Check if it's a date range (contains " - ")
    if (dateStr.includes(' - ')) {
      const parts = dateStr.split(' - ');
      if (parts.length === 2) {
        const startStr = parts[0].trim();
        const endStr = parts[1].trim();
        
        // Try to parse both dates
        const startDate = new Date(startStr);
        const endDate = new Date(endStr);
        
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const startYear = startDate.getFullYear();
          const endYear = endDate.getFullYear();
          
          if (startYear === endYear) {
            // Same year: "20 January - 30 January 2026"
            const startFormatted = startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
            const endFormatted = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
            return `${startFormatted} - ${endFormatted}`;
          } else {
            // Different years: "20 December 2025 - 10 January 2026"
            const startFormatted = startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
            const endFormatted = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
            return `${startFormatted} - ${endFormatted}`;
          }
        }
        // If parsing failed, return the original string
        return dateStr;
      }
    }
    
    // Single date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    return dateStr;
  };

  // Special card design for job listings (no image)
  if (listing.listing_type === 'job') {
    const jobCardClass = listing.plan === 'premium' 
      ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-amber-400 shadow-amber-100'
      : listing.plan === 'enhanced'
      ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-blue-300'
      : 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-slate-100';

    return (
      <Link to={listingUrl} data-testid={`listing-card-${listing.id}`}>
        <Card className={jobCardClass}>
          <div className="bg-green-50 p-3 md:p-4 border-b border-green-100">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm md:text-lg text-slate-900 group-hover:text-green-700 transition-colors line-clamp-2 mb-1">
                  {listing.title}
                </h3>
                {listing.company_name && (
                  <div className="flex items-center gap-1.5 text-xs md:text-sm text-slate-600">
                    <Building2 className="h-3 w-3 md:h-3.5 md:w-3.5 text-green-600 flex-shrink-0" />
                    <span className="truncate">{listing.company_name}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {listing.plan === 'premium' && (
                  <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-none shadow-sm text-xs">
                    <Crown className="h-3 w-3 mr-1" /> Premium
                  </Badge>
                )}
                {listing.plan === 'enhanced' && (
                  <Badge className="bg-blue-500 text-white border-none shadow-sm text-xs">
                    <Zap className="h-3 w-3 mr-1" /> Enhanced
                  </Badge>
                )}
                {listing.is_outside_essex && (
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-100 text-[10px] font-normal gap-1" data-testid="non-essex-badge">
                    <Globe className="h-2.5 w-2.5" />
                    Based in {listing.region || 'UK'} &middot; Serves Essex
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <CardContent className="p-3 md:p-4">
            {/* Description excerpt */}
            {listing.description && (
              <p className="text-xs md:text-sm text-slate-600 line-clamp-2 mb-2 md:mb-3">
                {stripTags(listing.description)}
              </p>
            )}
            
            {/* Salary - prominent display */}
            {(listing.salary || listing.hide_salary) && (
              <div className="mb-2 md:mb-3">
                <span className="text-sm md:text-base font-semibold text-green-700">
                  {listing.hide_salary ? 'Competitive Salary' : formatSalary(listing.salary)}
                </span>
                {!listing.hide_salary && listing.salary_type && (
                  <span className="text-xs md:text-sm text-slate-500 ml-1">
                    {listing.salary_type === 'hourly' ? 'per hour' : 'per year'}
                  </span>
                )}
              </div>
            )}
            
            {/* Location */}
            <div className="flex items-center gap-1.5 text-xs md:text-sm text-slate-600 mb-2 md:mb-3">
              <MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 text-slate-500" />
              <span className="truncate">
                {listing.is_remote ? 'Remote' : 
                 listing.is_hybrid ? `Hybrid - ${listing.town}` : 
                 listing.is_outside_essex && listing.region && listing.region !== 'Essex' ? listing.region :
                 listing.town}
              </span>
            </div>
            
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              {listing.is_remote && (
                <Badge variant="secondary" className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700">
                  Remote
                </Badge>
              )}
              {listing.is_hybrid && (
                <Badge variant="secondary" className="text-xs bg-sky-100 hover:bg-sky-200 text-sky-700">
                  Hybrid
                </Badge>
              )}
              {listing.job_type && (
                <Badge variant="secondary" className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700">
                  {formatJobType(listing.job_type)}
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-700">
                {formatCategory(listing.category)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Special card design for event listings
  if (listing.listing_type === 'event') {
    const eventCardClass = listing.plan === 'premium' 
      ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-amber-400 shadow-amber-100'
      : listing.plan === 'enhanced'
      ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-blue-300'
      : 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-slate-100';

    const eventImageUrl = cardThumbnail(listing.image_url) || getPlaceholderImage(listing.listing_type, listing.category);
    const eventImageSrcSet = cardThumbnailSrcSet(listing.image_url);

    return (
      <Link to={listingUrl} data-testid={`listing-card-${listing.id}`}>
        <Card className={eventCardClass}>
          {/* Event Image */}
          {!hideImages && (
          <div className={`aspect-[4/3] overflow-hidden relative ${listing.image_fill ? 'bg-white' : 'bg-slate-100'}`}>
            <img
              src={eventImageUrl}
              srcSet={eventImageSrcSet}
              sizes="(max-width: 768px) 50vw, 33vw"
              alt={`${listing.title} — ${listing.category || listing.listing_type} in ${listing.town || 'Essex'}`}
              loading="lazy"
              className={`listing-card-image w-full h-full ${listing.image_fill ? 'object-contain p-2' : 'object-cover'}`}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Plan badge */}
            {listing.plan === 'premium' && (
              <Badge className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-none shadow-lg">
                <Crown className="h-3 w-3 mr-1" /> Premium
              </Badge>
            )}
            {listing.plan === 'enhanced' && (
              <Badge className="absolute top-3 left-3 bg-blue-500 text-white border-none shadow-md">
                <Zap className="h-3 w-3 mr-1" /> Enhanced
              </Badge>
            )}
            
            {/* Event badge */}
            <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-purple-50 text-purple-700 border-purple-200">
              <Calendar className="h-3.5 w-3.5" />
              <span>Event</span>
            </div>
          </div>
          )}

          <CardContent className="p-3 md:p-4">
            {/* Title */}
            <h3 className="font-semibold text-sm md:text-lg text-slate-900 group-hover:text-purple-700 transition-colors line-clamp-2 mb-1.5 md:mb-2">
              {listing.title}
            </h3>
            
            {/* Event Date - Prominent display, single line on mobile */}
            {listing.event_date && (
              <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3 text-purple-700">
                <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                <span className="font-semibold text-xs md:text-base whitespace-nowrap truncate">{formatEventDate(listing.event_date)}</span>
                {(listing.event_start_time || listing.event_end_time) && (
                  <span className="hidden md:inline text-sm text-purple-500">
                    {listing.event_start_time && listing.event_end_time
                      ? `${formatTime12(listing.event_start_time)} - ${formatTime12(listing.event_end_time)}`
                      : listing.event_start_time
                        ? `From ${formatTime12(listing.event_start_time)}`
                        : `Until ${formatTime12(listing.event_end_time)}`
                    }
                  </span>
                )}
              </div>
            )}
            
            {/* Description excerpt */}
            {listing.description && (
              <p className="text-xs md:text-sm text-slate-600 line-clamp-2 mb-2 md:mb-3">
                {stripTags(listing.description)}
              </p>
            )}
            
            {/* Location */}
            <div className="flex items-center gap-1.5 text-xs md:text-sm text-slate-600 mb-2 md:mb-3">
              <MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 text-slate-600" />
              <span className="truncate">{listing.town}</span>
            </div>
            
            {/* Category badge */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700">
                {formatCategory(listing.category)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Special card design for classified listings
  if (listing.listing_type === 'classified') {
    const classifiedCardClass = listing.marketplace_featured 
      ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-amber-400 shadow-amber-100'
      : listing.marketplace_urgent
      ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-red-400 shadow-red-100'
      : 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-slate-100';

    const classifiedImageUrl = (uniformAspect ? cardThumbnail(listing.image_url) : cardThumbnailSquare(listing.image_url)) || getPlaceholderImage(listing.listing_type, listing.category);
    const classifiedImageSrcSet = uniformAspect ? cardThumbnailSrcSet(listing.image_url) : cardThumbnailSquareSrcSet(listing.image_url);
    
    // Format price display
    const getClassifiedPrice = () => {
      if (listing.status === 'sold') return 'SOLD';
      if (listing.classified_price === 0 || listing.price === 0) return 'Free';
      if (listing.classified_price) return `£${Number(listing.classified_price).toFixed(2)}`;
      if (listing.price) return `£${Number(listing.price).toFixed(2)}`;
      return 'Contact for price';
    };

    return (
      <Link to={listingUrl} data-testid={`listing-card-${listing.id}`}>
        <Card className={classifiedCardClass}>
          {/* Classified Image - Square aspect for better phone photo display, 4/3 when uniform */}
          {!hideImages && (
          <div className={`${uniformAspect ? 'aspect-[4/3]' : 'aspect-square'} overflow-hidden relative ${listing.image_fill ? 'bg-white' : 'bg-slate-100'}`}>
            <img
              src={classifiedImageUrl}
              srcSet={classifiedImageSrcSet}
              sizes="(max-width: 768px) 50vw, 33vw"
              alt={`${listing.title} — ${listing.category || listing.listing_type} in ${listing.town || 'Essex'}`}
              loading={isAboveFold ? 'eager' : 'lazy'}
              fetchPriority={isAboveFold ? 'high' : undefined}
              className={`listing-card-image w-full h-full ${listing.image_fill ? 'object-contain p-2' : 'object-cover'}`}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Marketplace upgrade badges */}
            {listing.marketplace_featured && (
              <Badge className="absolute top-3 left-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-none shadow-lg">
                <Crown className="h-3 w-3 mr-1" /> Featured
              </Badge>
            )}
            {listing.marketplace_urgent && (
              <Badge className={`absolute ${listing.marketplace_featured ? 'top-3 left-[110px]' : 'top-3 left-3'} bg-red-600 text-white border-none shadow-md`}>
                <Zap className="h-3 w-3 mr-1" /> Urgent
              </Badge>
            )}
            
            {/* SOLD overlay */}
            {listing.status === 'sold' && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white text-2xl font-bold bg-red-600 px-4 py-2 rounded-lg">SOLD</span>
              </div>
            )}
            
            {/* Classified badge */}
            <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-orange-50 text-orange-700 border-orange-200">
              <Tag className="h-3.5 w-3.5" />
              <span>Marketplace</span>
            </div>
          </div>
          )}

          <CardContent className="p-3 md:p-4">
            {/* Title */}
            <h3 className="font-semibold text-sm md:text-lg text-slate-900 group-hover:text-orange-700 transition-colors line-clamp-2 mb-1.5 md:mb-2">
              {listing.title}
            </h3>
            
            {/* Price - Prominent display */}
            <div className="mb-2 md:mb-3">
              <span className={`text-base md:text-xl font-bold ${listing.status === 'sold' ? 'text-red-600' : (listing.classified_price != null || listing.price != null) ? 'text-green-700' : 'text-slate-600'}`}>
                {getClassifiedPrice()}
              </span>
              {listing.accepts_offers && listing.status !== 'sold' && (listing.classified_price || listing.price) && (
                <Badge variant="secondary" className="ml-2 text-[10px] md:text-xs bg-green-100 text-green-700">
                  Accepts Offers
                </Badge>
              )}
            </div>
            
            {/* Description excerpt */}
            {listing.description && (
              <p className="text-xs md:text-sm text-slate-600 line-clamp-2 mb-2 md:mb-3">
                {stripTags(listing.description)}
              </p>
            )}
            
            {/* Location */}
            <div className="flex items-center gap-1.5 text-xs md:text-sm text-slate-600 mb-2 md:mb-3">
              <MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 text-slate-600" />
              <span className="truncate">{listing.town}</span>
            </div>
            
            {/* Condition and Category badges */}
            <div className="flex items-center gap-2 flex-wrap">
              {listing.classified_condition && listing.status !== 'sold' && (
                <Badge variant="secondary" className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 capitalize">
                  {formatCondition(listing.classified_condition)}
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700">
                {formatCategory(listing.category)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Standard card design for other listing types (with image)
  const imageUrl = cardThumbnail(listing.image_url) || getPlaceholderImage(listing.listing_type, listing.category);
  const imageSrcSet = cardThumbnailSrcSet(listing.image_url);

  // Premium listings get special border styling
  const cardClass = listing.plan === 'premium' 
    ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-amber-400 shadow-amber-100'
    : listing.plan === 'enhanced'
    ? 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-2 border-blue-300'
    : 'listing-card overflow-hidden hover:shadow-lg transition-all duration-300 group h-full border-slate-100';

  return (
    <Link to={listingUrl} data-testid={`listing-card-${listing.id}`}>
      <Card className={cardClass}>
        {!hideImages && (
        <div className={`aspect-square md:aspect-[4/3] overflow-hidden relative ${listing.image_fill ? 'bg-white' : 'bg-slate-100'}`}>
          <img
            src={imageUrl}
            srcSet={imageSrcSet}
            sizes="(max-width: 768px) 50vw, 33vw"
            alt={listing.title}
            loading={isAboveFold ? 'eager' : 'lazy'}
            fetchPriority={isAboveFold ? 'high' : undefined}
            className={`listing-card-image w-full h-full ${listing.image_fill ? 'object-contain p-2' : 'object-cover'}`}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Plan badge */}
          {listing.plan === 'premium' && (
            <Badge className="absolute top-2 left-2 md:top-3 md:left-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-none shadow-lg text-[10px] md:text-xs px-1.5 md:px-2.5">
              <Crown className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5 md:mr-1" /> Premium
            </Badge>
          )}
          {listing.plan === 'enhanced' && (
            <Badge className="absolute top-2 left-2 md:top-3 md:left-3 bg-blue-500 text-white border-none shadow-md text-[10px] md:text-xs px-1.5 md:px-2.5">
              <Zap className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5 md:mr-1" /> Enhanced
            </Badge>
          )}
          
          {/* Type badge - hidden on mobile to save space, visible on desktop */}
          <div className={`hidden md:flex absolute bottom-3 left-3 items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getTypeColor()}`}>
            {getTypeIcon()}
            <span className="capitalize">
              {listing.listing_type === 'classified' ? 'Marketplace' : listing.listing_type}
            </span>
          </div>
        </div>
        )}
        <CardContent className="p-2.5 md:p-4">
          <h3 className="font-semibold text-xs md:text-base text-slate-900 group-hover:text-[#0F2C3E] transition-colors line-clamp-2 md:line-clamp-1 mb-1 md:mb-2 leading-snug">
            {listing.title}
            {listing.verified && (
              <span className="inline-flex items-center justify-center ml-1 w-3.5 h-3.5 md:w-5 md:h-5 bg-blue-500 rounded-full align-middle cursor-help" title="Verified by Discover Essex" data-testid="verified-badge-card">
                <svg className="h-2 w-2 md:h-3 md:w-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              </span>
            )}
          </h3>
          
          <div className="flex items-center gap-1 md:gap-2 mb-1.5 md:mb-3">
            {(listing.listing_type === 'business' || listing.listing_type === 'event') && (
              <>
                {(listing.review_count || 0) === 0 ? (
                  <span className="text-[10px] md:text-xs text-slate-400 italic">No reviews yet</span>
                ) : (
                  <>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-2.5 w-2.5 md:h-3.5 md:w-3.5 ${star <= (listing.rating || 0) ? 'fill-[#D4A017] text-[#D4A017]' : 'text-slate-200'}`}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] md:text-xs text-slate-500">({listing.review_count})</span>
                    {(listing.review_count || 0) >= 5 && (
                      <span className="hidden md:inline text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200" data-testid="popular-badge">Popular</span>
                    )}
                  </>
                )}
              </>
            )}
            {listing.listing_type === 'classified' && (listing.price || listing.classified_price) && (
              <span className="text-xs md:text-sm font-semibold text-green-700">
                £{Number(listing.price || listing.classified_price).toFixed(2)}
              </span>
            )}
            {listing.listing_type === 'job' && (listing.salary || listing.hide_salary) && (
              <span className="text-xs md:text-sm font-medium text-slate-700">
                {listing.hide_salary ? 'Competitive' : formatSalary(listing.salary)}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-1 md:gap-1.5 text-[11px] md:text-sm text-slate-600 mb-1.5 md:mb-3">
            <MapPin className="h-3 w-3 md:h-3.5 md:w-3.5 text-[#4A6741] flex-shrink-0" />
            <span className="truncate">
              {listing.listing_type === 'job' && listing.is_remote ? 'Remote' : 
               listing.listing_type === 'job' && listing.is_hybrid ? `Hybrid - ${listing.town}` : 
               listing.is_outside_essex && listing.region && listing.region !== 'Essex' ? listing.region :
               listing.town}
            </span>
            {listing.is_outside_essex && (
              <span
                className="hidden md:inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-1.5 py-0.5 ml-1 shrink-0"
                data-testid="non-essex-badge-grid"
              >
                <Globe className="h-2.5 w-2.5" />
                UK-wide
              </span>
            )}
          </div>

          {/* Live Open-now / Closed pill — hidden on mobile 2-col for space */}
          {listing.listing_type === 'business' && listing.opening_hours && (
            <div className="hidden md:block mb-3">
              <OpeningHoursStatus openingHours={listing.opening_hours} />
            </div>
          )}
          
          {/* Category badge — compact on mobile */}
          <div className="flex items-center gap-1 md:gap-2 flex-wrap">
            {listing.listing_type === 'job' && listing.is_remote && (
              <Badge variant="secondary" className="hidden md:inline-flex text-xs bg-purple-100 hover:bg-purple-200 text-purple-800">Remote</Badge>
            )}
            {listing.listing_type === 'job' && listing.is_hybrid && (
              <Badge variant="secondary" className="hidden md:inline-flex text-xs bg-sky-100 hover:bg-sky-200 text-sky-800">Hybrid</Badge>
            )}
            {listing.listing_type === 'job' && listing.job_type && (
              <Badge variant="secondary" className={`hidden md:inline-flex text-xs ${getJobTypeBadgeColor(listing.job_type)}`}>{formatJobType(listing.job_type)}</Badge>
            )}
            {!hideCategoryBadge && (
              <Badge variant="secondary" className="text-[10px] md:text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-1.5 md:px-2.5">
                {listing.category}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ListingCard;